import React from 'react';
import { UserVideo } from '../../api/videoApi';

// Legacy interface for backward compatibility
export interface UploadingVideo {
  id: string;
  prompt: string;
  model: string;
  status: 'uploading' | 'processing' | 'completed' | 'failed';
  createdAt: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
  uploadStartTime?: number;
  progress?: number;
}

// Interface for upload state
export interface UploadState {
  uploading: boolean;
  uploadProgress: number;
  uploadError: string | null;
  uploadSuccess: boolean;
}

// Optimistic upload functions
export const createOptimisticUploadVideo = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>
) => {
  return (videoFile: File, videoTitle: string, _thumbnailFile: File) => {
    const optimisticVideo: UserVideo = {
      id: `uploading_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      video_url: '',
      thumbnail_url: '',
      title: videoTitle,
      file_size: videoFile.size,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      user_id: '',
      status: 'uploading',
      prompt: videoTitle
    };

    setVideos(prev => [optimisticVideo, ...prev]);
    return optimisticVideo;
  };
};

export const createOptimisticUpdateUploadVideo = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>
) => {
  return (videoId: string, updates: Partial<UserVideo>) => {
    setVideos(prev => prev.map(v => 
      v.id === videoId 
        ? { ...v, ...updates }
        : v
    ));
  };
};

export const createOptimisticRemoveUploadVideo = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>
) => {
  return (videoId: string) => {
    setVideos(prev => prev.filter(v => v.id !== videoId));
  };
};

// Upload handler with optimistic updates
export const createUploadHandler = (
  optimisticAddVideo: (videoFile: File, videoTitle: string, thumbnailFile: File) => UserVideo,
  optimisticUpdateVideo: (videoId: string, updates: Partial<UserVideo>) => void,
  optimisticRemoveVideo: (videoId: string) => void,
  setUploadState: React.Dispatch<React.SetStateAction<UploadState>>,
  uploadFunction: (video: File, videoTitle: string, thumbnailFile: File) => Promise<any>
) => {
  return async (videoFile: File, videoTitle: string, thumbnailFile: File): Promise<{ success: boolean; error?: string }> => {
    // Reset upload state
    setUploadState({
      uploading: true,
      uploadProgress: 0,
      uploadError: null,
      uploadSuccess: false
    });

    // Create optimistic video
    const optimisticVideo = optimisticAddVideo(videoFile, videoTitle, thumbnailFile);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadState(prev => ({
          ...prev,
          uploadProgress: Math.min(prev.uploadProgress + 10, 90)
        }));
      }, 200);

      const result = await uploadFunction(videoFile, videoTitle, thumbnailFile);
      clearInterval(progressInterval);

      if (result.video_info) {
        // Success - update the optimistic video with real data
        optimisticUpdateVideo(optimisticVideo.id, {
          ...result.video_info,
          status: 'completed'
        });

        setUploadState({
          uploading: false,
          uploadProgress: 100,
          uploadError: null,
          uploadSuccess: true
        });

        return { success: true };
      } else {
        // Error - remove optimistic video
        optimisticRemoveVideo(optimisticVideo.id);
        
        setUploadState({
          uploading: false,
          uploadProgress: 0,
          uploadError: 'Upload failed. Please try again.',
          uploadSuccess: false
        });

        return { success: false, error: 'Upload failed. Please try again.' };
      }
    } catch (error) {
      // Error - remove optimistic video
      optimisticRemoveVideo(optimisticVideo.id);

      setUploadState({
        uploading: false,
        uploadProgress: 0,
        uploadError: error instanceof Error ? error.message : 'An unexpected error occurred',
        uploadSuccess: false
      });

      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    }
  };
};

// Hook factory for optimistic upload functions
export const useOptimisticUpload = (
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>,
  setUploadState: React.Dispatch<React.SetStateAction<UploadState>>,
  uploadFunction: (video: File, videoTitle: string, thumbnailFile: File) => Promise<any>
) => {
  const optimisticAddVideo = React.useCallback(
    createOptimisticUploadVideo(setVideos),
    [setVideos]
  );

  const optimisticUpdateVideo = React.useCallback(
    createOptimisticUpdateUploadVideo(setVideos),
    [setVideos]
  );

  const optimisticRemoveVideo = React.useCallback(
    createOptimisticRemoveUploadVideo(setVideos),
    [setVideos]
  );

  const handleUpload = React.useCallback(
    createUploadHandler(optimisticAddVideo, optimisticUpdateVideo, optimisticRemoveVideo, setUploadState, uploadFunction),
    [optimisticAddVideo, optimisticUpdateVideo, optimisticRemoveVideo, setUploadState, uploadFunction]
  );

  return {
    optimisticAddVideo,
    optimisticUpdateVideo,
    optimisticRemoveVideo,
    handleUpload
  };
};

// Legacy hook for backward compatibility with UploadingVideo interface
export const useOptimisticUploadLegacy = (
  setUploadingVideos: React.Dispatch<React.SetStateAction<UploadingVideo[]>>,
  setVideos: React.Dispatch<React.SetStateAction<UserVideo[]>>
) => {
  const optimisticAddUploadingVideo = React.useCallback(
    (prompt: string, model: string, file: File) => {
      const uploadingVideo: UploadingVideo = {
        id: `uploading_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
        prompt: prompt.trim(),
        model,
        status: 'uploading',
        createdAt: new Date().toISOString(),
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        uploadStartTime: Date.now(),

      };

      setUploadingVideos(prev => [uploadingVideo, ...prev]);
      return uploadingVideo;
    },
    [setUploadingVideos]
  );

  const optimisticUpdateUploadingVideo = React.useCallback(
    (videoId: string, updates: Partial<UploadingVideo>) => {
      setUploadingVideos(prev => prev.map(v => 
        v.id === videoId 
          ? { ...v, ...updates }
          : v
      ));
    },
    [setUploadingVideos]
  );

  const optimisticRemoveUploadingVideo = React.useCallback(
    (videoId: string) => {
      setUploadingVideos(prev => prev.filter(v => v.id !== videoId));
    },
    [setUploadingVideos]
  );

  const optimisticAddCompletedVideo = React.useCallback(
    (uploadedVideo: UserVideo) => {
      setVideos(prev => [uploadedVideo, ...prev]);
    },
    [setVideos]
  );

  return {
    optimisticAddUploadingVideo,
    optimisticUpdateUploadingVideo,
    optimisticRemoveUploadingVideo,
    optimisticAddCompletedVideo
  };
};
